import * as React from "react";
import * as Icon from "@ant-design/icons";
import {
  Button,
  Input,
  Typography,
  Spin,
  Steps,
  message,
  Tag,
  Divider,
} from "antd";
import { useOrg } from "src/hooks/useOrg";
import {
  createSource,
  getSource,
  updateSource,
} from "pages/api-clients/integrations";
import {
  Source,
  SourceType,
} from "pages/api/[orgId]/integrations-client/definitions";
import { Terminology } from "./Terminology";

const { Text, Title } = Typography;

enum IntegrationState {
  fetching,
  none,
  creating,
  active,
  savingJira,
}

export function JiraIntegrationStatus() {
  const { id: orgId } = useOrg();
  const [jiraSource, setJiraSource] = React.useState<Source | null>(null);
  const [state, setState] = React.useState<IntegrationState>(
    IntegrationState.fetching
  );
  const [authToken, setAuthToken] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [host, setHost] = React.useState("");
  const fetchedOnce = React.useRef(false);

  const listWebhookItems = [
    {
      description: (
        <Text>
          Make sure you have Jira admin permissions. <br />
          Click on the Gear icon <Icon.SettingOutlined /> on the top right {">"}
          Jira Settings {">"} System
          <br />
          On the left side, scroll down and click &apos;Webhook&apos;.
        </Text>
      ),
    },
    {
      description: (
        <div>
          Click on &quot;Create Webhook&quot;
          <ul>
            <li>Name your webhook</li>
            <li>
              For <strong>URL</strong> use:
              <Text code copyable>
                {`https://int.vzbl.dev/api/v1/jira/webhook/${jiraSource?.uuid}`}
              </Text>
            </li>
            <li>Enable all events</li>
          </ul>
        </div>
      ),
    },
    { description: <p>Click Save</p> },
  ];

  const listApiTokenItems = [
    {
      description: (
        <>
          Proceed to API token creation by accessing&nbsp;
          <a
            href="https://id.atlassian.com/manage-profile/security/api-tokens"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://id.atlassian.com/manage-profile/security/api-tokens
          </a>
        </>
      ),
    },
    {
      description:
        "Create an API token. Paste your jira host (e.g. 'vzbl-inc.atlassian.net'), your jira login (typically your email address) and your token in the fields below",
    },
    { description: "Save your API token" },
  ];

  React.useEffect(() => {
    if (!fetchedOnce.current) {
      return;
    }

    if (jiraSource == null) {
      setState(IntegrationState.none);
    } else {
      setState(IntegrationState.active);
    }
  }, [jiraSource]);

  const transitionRef = React.useRef({ jiraSource, orgId });
  transitionRef.current = { jiraSource, orgId };
  React.useEffect(() => {
    const { jiraSource, orgId } = transitionRef.current;
    if (!orgId) return;
    switch (state) {
      case IntegrationState.fetching: {
        getSource(orgId, SourceType.Jira).then((payload) => {
          const source = payload.data?.[0];
          fetchedOnce.current = true;
          setJiraSource(source);
          setAuthToken(source?.authToken || "");
          setUsername(source?.username || "");
          setHost(source?.host || "");
        });
        break;
      }
      case IntegrationState.creating: {
        createSource(orgId, SourceType.Jira).then((payload) => {
          setJiraSource(payload.data);
        });
        break;
      }

      case IntegrationState.savingJira: {
        updateSource(orgId, SourceType.Jira, {
          id: jiraSource!.id,
          authToken,
          username,
          host,
        }).then((payload) => {
          setJiraSource(payload.data);
          setState(IntegrationState.fetching);
          message.success("Successfully updated jira integration");
        });
        break;
      }
    }
  }, [state, authToken, host, username]);

  return [
    IntegrationState.fetching,
    IntegrationState.creating,
    IntegrationState.savingJira,
  ].includes(state) ? (
    <Spin />
  ) : state == IntegrationState.none ? (
    <Button
      onClick={() => {
        setState(IntegrationState.creating);
      }}
    >
      Create Jira Integration
    </Button>
  ) : (
    <div>
      <Terminology>
        <p>
          This page will guide you through setting up a webhook and/or API
          token.
        </p>
        <p>
          <Tag bordered={false}>Jira Webhook</Tag>
          <ul>
            <li>Follow steps to create a webhook in Jira</li>
            <li>Used to obtain always up-to-date data</li>
          </ul>
          <Tag bordered={false}>API Token</Tag>
          <ul>
            <li>Follow steps to create and save a token and details</li>
            <li>Used to obtain historical data</li>
          </ul>
        </p>
        <p>
          One may be enough to get started, but for best results set up both.
        </p>
      </Terminology>
      <Divider />

      <h3>
        <Tag bordered={false}>Jira Webhook</Tag> configuration
      </h3>

      <div style={{ padding: "16px 40px" }}>
        <Steps
          direction="vertical"
          progressDot
          current={listWebhookItems.length - 1}
          items={listWebhookItems}
        />
      </div>

      <Divider />
      <h3>
        <Tag bordered={false}>API Token</Tag> configuration
      </h3>

      <div style={{ padding: "16px 40px" }}>
        <Steps
          direction="vertical"
          progressDot
          current={listApiTokenItems.length - 1}
          items={listApiTokenItems}
        />
      </div>
      <Title level={5}>Host</Title>
      <Input
        allowClear
        defaultValue={host}
        style={{ width: "80%" }}
        onChange={(event) => setHost(event.target.value)}
      />
      <Title level={5}>Email</Title>
      <Input
        allowClear
        defaultValue={username}
        style={{ width: "80%" }}
        onChange={(event) => setUsername(event.target.value)}
      />
      <Title level={5}>API token</Title>
      <Input.Password
        allowClear
        visibilityToggle={authToken != "placeholder"}
        defaultValue={authToken}
        style={{ width: "80%" }}
        onChange={(event) => setAuthToken(event.target.value)}
      />
      <Button
        type="primary"
        style={{ marginLeft: "10px" }}
        onClick={() => {
          setState(IntegrationState.savingJira);
        }}
        disabled={!authToken || !username || !host}
      >
        Save
      </Button>
    </div>
  );
}
